/**
 * format.js — every user-facing value in the app is rendered through here.
 *
 * Contract: docs/design-system/TOKEN_CONTRACT.md §12
 *
 * ── WHY ─────────────────────────────────────────────────────────────────────
 *
 * Measured 2026-09-01: 407 inline `toLocale*` calls across 220 files, in a
 * dozen different shapes. 31 of them were a bare `toLocaleDateString()` — no
 * locale, no timezone — which renders in whatever the viewer's machine is set
 * to, so the same enrolment date read differently on two desks in one office.
 * A further split had half the call sites pinning `Asia/Kolkata` and half not,
 * which is how a class scheduled for 00:30 IST showed as the previous day.
 *
 * There is no way to make 407 of those agree by review. There is one way to
 * make them agree, which is for there to be one of them.
 *
 * ── THE TIMEZONE DECISION IS ALREADY MADE ───────────────────────────────────
 *
 * IST, always, regardless of the viewer's browser. That is not a new call — it
 * is what utils/common.js has said since it was written ("All display
 * timestamps use Asia/Kolkata regardless of the user's browser TZ") and what
 * the majority of the pinned call sites already do. It is also the right one
 * for this product: an institute's timetable is set in Indian local time, and a
 * class that starts at 10:00 must read 10:00 to the trainer running it and to
 * the student joining from anywhere. Pass `timeZone` to override for the rare
 * screen that genuinely means the viewer's own clock.
 *
 * ⚠️ "Today" and "Yesterday" are therefore computed against the IST calendar
 * too. Deriving them from the viewer's local date instead would print "Today"
 * on a row that IST calls tomorrow — the same off-by-one this file exists to
 * remove, reintroduced one layer up.
 *
 * ── EVERY FORMATTER RETURNS AN EM DASH FOR A MISSING VALUE ──────────────────
 *
 * Not "", not "N/A", not "Invalid Date". One placeholder, so a blank cell in a
 * table always means "we have no value" and never means "the formatter threw".
 *
 * ⚠️ utils/common.js returns "" instead, and deliberately still does: roughly
 * 200 call sites read those, and some are written `{formatDateIST(x) || "N/A"}`
 * — a fallback that an em dash, being truthy, would silently disable. Those
 * functions are now thin wrappers over this file, so there is one implementation
 * with two empty-value contracts, and a screen moves to this one when it is
 * converted rather than all at once.
 */

/** The one placeholder for a value we do not have. */
export const DASH = "—";

const IST = "Asia/Kolkata";
const LOCALE = "en-IN";

/**
 * Every entry point starts here. `new Date(undefined)` is an Invalid Date and
 * `new Date(null)` is the epoch — two different wrong answers for the same
 * missing value — so both are rejected before the cast.
 */
const toDate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** A finite number, or null. Numeric strings are accepted; "" and null are not. */
const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : Number(String(value).replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
};

/**
 * The calendar date a moment falls on in a given zone, as "YYYY-MM-DD".
 * `en-CA` is the trick: it is the one common locale whose short date format is
 * already ISO order, so the strings sort and compare directly.
 */
const dayKey = (date, timeZone) =>
  new Intl.DateTimeFormat("en-CA", { timeZone }).format(date);

// ── Dates and times ─────────────────────────────────────────────────────────

/**
 * "23 Feb 2026". The house date format.
 *
 *   formatDate(enrolledAt)                    → "23 Feb 2026"
 *   formatDate(d, { year: undefined })        → "23 Feb"
 *   formatDate(d, { timeZone: undefined })    → the viewer's own zone
 */
export const formatDate = (value, opts = {}) => {
  const d = toDate(value);
  if (!d) return DASH;
  return d.toLocaleDateString(LOCALE, {
    day: "numeric", month: "short", year: "numeric", timeZone: IST, ...opts,
  });
};

/** "02:30 PM". */
export const formatTime = (value, opts = {}) => {
  const d = toDate(value);
  if (!d) return DASH;
  return d.toLocaleTimeString(LOCALE, {
    hour: "2-digit", minute: "2-digit", hour12: true, timeZone: IST, ...opts,
  });
};

/**
 * "10:00" → "10:00 am" — a time of day stored as a 24-hour "HH:mm" string, the
 * way a batch timetable is (`Batch.startTime`). It has no date and no zone, so
 * it is read as a wall-clock time and never shifted.
 */
export const formatTimeOfDay = (value) => {
  const match = /^(\d{1,2}):(\d{2})/.exec(String(value ?? "").trim());
  if (!match) return DASH;
  const [h, m] = [Number(match[1]), Number(match[2])];
  if (h > 23 || m > 59) return DASH;
  return formatTime(new Date(Date.UTC(2000, 0, 1, h, m)), { timeZone: "UTC" });
};

/** "23 Feb 2026, 02:30 PM". The absolute form — no "Today". */
export const formatDateTime = (value, opts = {}) => {
  const d = toDate(value);
  if (!d) return DASH;
  return d.toLocaleString(LOCALE, {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true, timeZone: IST, ...opts,
  });
};

/**
 * "Today, 02:30 PM" · "Yesterday, 09:05 AM" · "23 Feb 2026, 02:30 PM".
 *
 * For anything a person reads as recent activity — a log row, a last-seen, an
 * inbox timestamp. Use formatDateTime where the exact date is the point (an
 * invoice, an exam window, an audit record): "Today" is friendlier and is
 * useless the day after someone screenshots it.
 */
export const formatDateTimeFriendly = (value, opts = {}) => {
  const d = toDate(value);
  if (!d) return DASH;
  const zone = "timeZone" in opts ? opts.timeZone : IST;
  const time = formatTime(d, { timeZone: zone });

  const now = new Date();
  const today = dayKey(now, zone);
  const yesterday = dayKey(new Date(now.getTime() - 86400000), zone);
  const key = dayKey(d, zone);

  if (key === today) return `Today, ${time}`;
  if (key === yesterday) return `Yesterday, ${time}`;
  return formatDateTime(d, opts);
};

/**
 * "just now" · "5m ago" · "3h ago" · "2d ago", then the absolute date.
 *
 * It stops being relative after a week on purpose: "43d ago" is arithmetic the
 * reader has to do, where "23 Feb 2026" is the answer.
 */
export const formatRelative = (value) => {
  const d = toDate(value);
  if (!d) return DASH;
  const diff = Date.now() - d.getTime();
  if (diff < 0) return "just now"; // clock skew between the box and the browser
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(d);
};

// ── Durations ───────────────────────────────────────────────────────────────

/**
 * A span a person reads: "1h 24m" · "24m 10s" · "45s" · "2d 3h".
 *
 * Two units at most. "1h 24m 09s" is a stopwatch reading, not a duration, and
 * the third unit is never what the sentence needed.
 *
 * @param {number} seconds
 */
export const formatDuration = (seconds) => {
  const n = toNumber(seconds);
  if (n === null || n < 0) return DASH;
  const s = Math.floor(n);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return s % 60 ? `${m}m ${s % 60}s` : `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return m % 60 ? `${h}h ${m % 60}m` : `${h}h`;
  const d = Math.floor(h / 24);
  return h % 24 ? `${d}d ${h % 24}h` : `${d}d`;
};

/**
 * "01:24:07" · "24:07" — a live timer or a video position, where the digits
 * must not change width as they count. Pair it with the `tabular` class.
 *
 * @param {number} seconds
 */
export const formatClock = (seconds) => {
  const n = toNumber(seconds);
  if (n === null || n < 0) return DASH;
  const s = Math.floor(n);
  const pad = (x) => String(x).padStart(2, "0");
  const h = Math.floor(s / 3600);
  const rest = `${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
  return h ? `${pad(h)}:${rest}` : rest;
};

// ── Numbers ─────────────────────────────────────────────────────────────────

/** "12,34,567" — Indian grouping, which is what en-IN gives. */
export const formatNumber = (value, opts = {}) => {
  const n = toNumber(value);
  if (n === null) return DASH;
  return n.toLocaleString(LOCALE, opts);
};

/**
 * "₹12,34,567.89". Indian lakh/crore grouping, not thousands.
 *
 * ⚠️ Fractions are dropped for whole amounts and kept for the rest, rather than
 * always showing ".00": a fees table of round numbers is easier to scan without
 * a column of zeroes, and a part-payment of ₹1,250.50 must never round to
 * ₹1,251 on screen while the ledger holds the paise.
 */
export const formatCurrency = (value, opts = {}) => {
  const n = toNumber(value);
  if (n === null) return DASH;
  return n.toLocaleString(LOCALE, {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2,
    ...opts,
  });
};

/**
 * "72%" · "72.5%".
 *
 * Takes a PERCENTAGE (72), not a fraction (0.72) — every score, attendance rate
 * and completion figure in this app already arrives from the API as the former,
 * and a helper that silently multiplied by 100 would be wrong
 * everywhere it was reached for.
 */
export const formatPercent = (value, digits = 0) => {
  const n = toNumber(value);
  if (n === null) return DASH;
  return `${n.toFixed(digits).replace(/\.0+$/, "")}%`;
};

/** "4.2 MB" — 1024-based, matching what the OS reports for the same upload. */
export const formatBytes = (value, digits = 1) => {
  const n = toNumber(value);
  if (n === null || n < 0) return DASH;
  if (n === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
  const scaled = n / 1024 ** i;
  return `${i === 0 ? scaled : scaled.toFixed(digits).replace(/\.0+$/, "")} ${units[i]}`;
};

// ── Text ────────────────────────────────────────────────────────────────────

/**
 * Any value that must not render as an empty cell. Use it for a name, an id, a
 * free-text note — anything that is already a string and only needs the
 * missing-value rule applied.
 */
export const orDash = (value) =>
  value === null || value === undefined || value === "" ? DASH : value;

export default {
  DASH,
  formatDate,
  formatTime,
  formatTimeOfDay,
  formatDateTime,
  formatDateTimeFriendly,
  formatRelative,
  formatDuration,
  formatClock,
  formatNumber,
  formatCurrency,
  formatPercent,
  formatBytes,
  orDash,
};
